import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HlmTooltipTriggerDirective } from '@spartan-ng/helm/tooltip';
import { MapPaint, SolarMap } from '@shared/components/solar-map/solar-map';
import { Tabs } from '@shared/components/tabs/tabs';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { AREA_METRICS, AreaMetric, binsOf, headline, streetOf, topStreets } from './campaign-area.model';

const percent = (part: number, whole: number) => {
  if (!whole) return '0 %';
  const share = (part / whole) * 100;
  return `${share < 10 && part ? share.toLocaleString('de-DE', { maximumFractionDigits: 1 }) : Math.round(share)} %`;
};

/** The area itself: the map shows one value at a time, the charts beside it explain and filter it. */
@Component({
  selector: 'app-campaign-area',
  standalone: true,
  imports: [DecimalPipe, HlmTooltipTriggerDirective, SolarMap, Tabs],
  templateUrl: './campaign-area.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignArea {
  readonly store = inject(CampaignWorkflowStore);
  readonly org = inject(CurrentOrganizationStore);

  readonly metrics = AREA_METRICS.map(({ id, label }) => ({ value: id, label }));
  readonly metric = signal<AreaMetric>('kwp');
  /** One bar or street picked in the charts; the map then shows only that. */
  readonly focus = signal<{ kind: 'bin' | 'street'; key: string } | null>(null);

  private readonly items = computed(() => this.store.items() ?? []);
  private readonly bins = computed(() => binsOf(this.metric()));
  readonly title = computed(() => AREA_METRICS.find((metric) => metric.id === this.metric())!.title);
  readonly figure = computed(() => headline(this.metric(), this.items()));
  readonly streets = computed(() => topStreets(this.items()));

  readonly rows = computed(() => {
    const items = this.items();
    const counted = this.bins().map((bin) => ({ ...bin, count: items.reduce((all, item) => all + (bin.test(item) ? 1 : 0), 0) }));
    const total = counted.reduce((all, row) => all + row.count, 0);
    const peak = Math.sqrt(Math.max(1, ...counted.map((row) => row.count)));
    return counted
      .filter((row) => this.metric() !== 'stage' || row.count)
      .map((row) => ({
        ...row,
        // ponytail: square root, so a few dozen stay visible beside thousands; the exact number stands beside the bar.
        width: row.count ? Math.max(2, (Math.sqrt(row.count) / peak) * 100) : 0,
        title: `${row.label}: ${row.count.toLocaleString('de-DE')} (${percent(row.count, total)})${row.tip ? `. ${row.tip}` : ''}`,
      }));
  });
  readonly withoutValue = computed(() => {
    if (this.metric() === 'stage') return 0;
    const bins = this.bins();
    return this.items().filter((item) => !bins.some((bin) => bin.test(item))).length;
  });

  readonly paint = computed<MapPaint>(() => {
    const bins = this.bins();
    const focus = this.focus();
    const colors = new Map<string, string>();
    for (const item of this.items()) {
      if (focus?.kind === 'street' && streetOf(item.title) !== focus.key) continue;
      const bin = bins.find((candidate) => candidate.test(item));
      if (bin && (focus?.kind !== 'bin' || bin.key === focus.key)) colors.set(item.analysisId, bin.color);
    }
    return (row) => colors.get(row.analysisId) ?? null;
  });

  pickMetric(metric: AreaMetric): void {
    this.metric.set(metric);
    this.focus.set(null);
  }

  toggle(kind: 'bin' | 'street', key: string): void {
    const focus = this.focus();
    this.focus.set(focus?.kind === kind && focus.key === key ? null : { kind, key });
  }
}
